import { useEffect } from 'react';
import SRError from '~/utils/errors/srError';
import isInputElement from '~/utils/isInputElement';

interface IUseOpenSERPListeners {
    hideBodyOverflow: () => void;
    openSERP: (initialQuery?: string) => void;
    persistenceInitialQuery: string;
    searchElementSelector: string;
    searchTriggerSelector?: string;
    isOpen: boolean;
    isInstantSearch: boolean;
}

/**
 * Handles logic of opening the SERP layer. Register paste, click, focus, touchend event listeners on elements that should trigger the opening.
 */
const useOpenSERPListeners = ({
    hideBodyOverflow,
    openSERP,
    persistenceInitialQuery,
    searchElementSelector,
    searchTriggerSelector,
    isOpen,
    isInstantSearch,
}: IUseOpenSERPListeners) => {
    useEffect(() => {
        const nativeInputElements: Element[] = Array.from(document.querySelectorAll(`${searchElementSelector}`));

        let searchTriggerElements: Element[] = [];
        if (searchTriggerSelector) {
            searchTriggerElements = Array.from(document.querySelectorAll(`${searchTriggerSelector}`));
        }

        if (nativeInputElements.length === 0) {
            throw new SRError('No native search element has been found found');
        }
        if (persistenceInitialQuery !== '') {
            hideBodyOverflow();
            openSERP();
        }

        if (isInstantSearch && searchTriggerSelector) {
            searchTriggerElements.forEach((searchTriggerElement: Element) => {
                searchTriggerElement.addEventListener('mousedown', () => {
                    nativeInputElements.forEach((nativeInputElement: Element) => {
                        if (isInputElement(nativeInputElement) && nativeInputElement.value !== '') {
                            openSERP(nativeInputElement.value);
                        }
                    });
                });
            });
        }

        nativeInputElements.forEach(
            (nativeInputElement: Element) => {
                if (isInputElement(nativeInputElement)) {
                    // open serp if native input is focused - for slower connections
                    if (nativeInputElement === document.activeElement) {
                        openSERP();
                    }

                    if (nativeInputElement.value !== '') {
                        openSERP(nativeInputElement.value);
                    }

                    nativeInputElement.addEventListener('paste', (event: Event) => {
                        if (!isInstantSearch) {
                            event.preventDefault();
                        }
                        const clipboardEvent = event as ClipboardEvent;
                        if (!clipboardEvent.clipboardData) {
                            return;
                        }
                        const clipboard = clipboardEvent.clipboardData.getData('text');
                        openSERP(clipboard);
                    });

                    nativeInputElement.addEventListener(
                        'focus',
                        (event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            setTimeout(() => {
                                // timeout is needed to ensure that acton is called after the click event
                                if (isOpen) {
                                    return;
                                }
                                openSERP();
                            }, 100);
                        },
                        true
                    );
                }

                nativeInputElement.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (isInputElement(nativeInputElement) && nativeInputElement.value !== '') {
                        openSERP((nativeInputElement as HTMLInputElement).value);
                        return;
                    }

                    openSERP();
                });

                nativeInputElement.addEventListener('touchend', (event) => {
                    // if SERP is operating in instant search mode, do not prevent default action,
                    // as it would prevent typing in the input field
                    // TODO: maybe completelly replacing the original input with the SERP input would serve better and we would not have to prevent the default action at all
                    if (!isInstantSearch) {
                        event.preventDefault();
                    }
                    event.stopPropagation();

                    openSERP();
                });

                nativeInputElement.addEventListener('keydown', (event) => {
                    if (!isOpen && !isInstantSearch) {
                        openSERP();
                    }
                });
            },
            [searchElementSelector]
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchElementSelector]);
};

export default useOpenSERPListeners;
